<template>
  <BlogPostLayout>
    <template #image>
      <BlogHeader
        :title="postData.title"
        :excerpt="postData.excerpt"
        :image="postData.image"
        :author="postData.author"
        :date="postData.date"
        :updatedAt="postData.updatedAt"
        :categories="postData.categories"
        :readTime="postData.readTime"
      />
    </template>
    <template #content>
      <article class="blog-content" lang="fr">
        <section class="intro">
          <p>
            Comment <strong>intégrer une visite virtuelle sur son site
            internet</strong> ? Les pages avec une visite 360° intégrée voient
            la durée moyenne de session passer de moins de 2 minutes à plus de 5
            minutes — un signal fort pour le référencement Google. Ce guide
            couvre l’iframe, WordPress, les CMS courants et l’optimisation des
            performances.
          </p>
        </section>

        <section>
          <h2>Comment fonctionne l'intégration d'une visite virtuelle</h2>

          <h3>La méthode iFrame</h3>
          <p>
            La méthode standard consiste à utiliser une balise HTML iFrame.
            L'iFrame charge le contenu de la visite (hébergé sur Travvir) à
            l'intérieur d'une fenêtre sur votre page. Voici la structure de base
            :
          </p>
          <pre><code>&lt;iframe
  src="https://app.travvir.com/tour/VOTRE-ID-VISITE"
  width="100%"
  height="500"
  frameborder="0"
  allowfullscreen
  allow="xr-spatial-tracking; gyroscope; accelerometer"
&gt;&lt;/iframe&gt;</code></pre>
          <p>
            L'attribut <code>allowfullscreen</code> active le mode plein écran.
            L'attribut <code>allow</code> accorde les permissions nécessaires
            pour le gyroscope sur mobile — indispensable pour une bonne
            expérience mobile.
          </p>

          <h3>Récupérer le code d'intégration depuis Travvir</h3>
          <p>
            Dans votre tableau de bord Travvir : sélectionnez votre visite →
            cliquez sur Partager → sélectionnez Intégrer → copiez le code iFrame
            fourni. Le code Travvir inclut déjà les permissions correctes et les
            ratios d'affichage préconfigurés.
          </p>
        </section>

        <section>
          <h2>Intégration par plateforme</h2>

          <h3>WordPress</h3>
          <p>
            Dans l'éditeur Gutenberg : ajoutez un bloc "HTML personnalisé" à
            l'endroit voulu, collez le code iFrame, puis publiez. N'utilisez pas
            le mode "HTML" d'un bloc paragraphe — il supprime les balises
            iFrame. Vous devez utiliser le bloc HTML dédié.
          </p>

          <h3>Squarespace</h3>
          <p>
            Ajoutez un bloc Embed à votre page, basculez sur l'option "Code
            d'intégration" (pas l'intégration par URL), et collez le code
            iFrame. Squarespace conserve les iFrames et les affiche
            correctement.
          </p>

          <h3>Wix</h3>
          <p>
            Dans l'éditeur Wix : Ajouter → Intégrer → HTML iFrame → collez votre
            code. Redimensionnez le composant sur le canevas pour ajuster les
            dimensions.
          </p>

          <h3>Webflow</h3>
          <p>
            Ajoutez un élément Embed dans votre canevas Webflow, collez le code
            iFrame dans le champ HTML. Webflow restitue les iFrames nativement
            avec interactivité complète, y compris le gyroscope sur mobile.
          </p>
        </section>

        <section>
          <h2>Optimiser l'intégration pour les performances</h2>

          <h3>Chargement différé (lazy loading)</h3>
          <p>
            Ajoutez <code>loading="lazy"</code> à votre balise iFrame pour que
            la visite ne se charge qu'au moment où l'utilisateur fait défiler
            jusqu'à elle. Cela améliore sensiblement le temps de chargement
            initial de la page.
          </p>

          <h3>Hauteur minimale recommandée</h3>
          <p>
            Définissez une hauteur d'au moins 500 px pour un confort de
            navigation sur desktop. Sur mobile, utilisez un ratio responsive via
            CSS (ex. : <code>padding-bottom: 56.25%</code>) pour que la visite
            s'adapte automatiquement à l'écran.
          </p>

          <h3>Performance et Core Web Vitals</h3>
          <p>
            Placez la visite sous le premier écran (below the fold) quand
            possible, utilisez le lazy loading, et évitez plusieurs iframes sur
            la même page. Une seule visite bien placée convertit mieux qu’une
            page alourdie par trois lecteurs 360°.
          </p>
        </section>

        <section>
          <h2>Intégrer une visite virtuelle sur WordPress (détail)</h2>
          <p>
            Sur WordPress, le bloc « HTML personnalisé » est la méthode la plus
            fiable. Collez le code iframe Travvir, publiez, puis testez en
            mobile. Si votre thème ou un plugin de sécurité bloque les iframes,
            autorisez le domaine Travvir ou utilisez le lien partageable en
            bouton CTA. Guide dédié :
            <a href="/blog/fr/visite-virtuelle-wordpress"
              >ajouter une visite virtuelle sur WordPress</a
            >.
          </p>
        </section>

        <section>
          <h2>Questions fréquentes</h2>

          <h3>L'intégration ralentit-elle mon site ?</h3>
          <p>
            Non si vous utilisez le lazy loading. La visite virtuelle ne se
            charge que lorsque le visiteur fait défiler jusqu'à elle, ce qui
            préserve la vitesse de chargement initial.
          </p>

          <h3>Puis-je intégrer plusieurs visites sur une même page ?</h3>
          <p>
            Techniquement oui, mais déconseillé. Privilégiez une visite par
            page, ou des onglets pour charger une seule visite à la fois.
          </p>
        </section>
        <section class="continue-reading">
          <h2>Continuez la Lecture</h2>
          <ul>
          <li><a href="/blog/fr/visite-virtuelle-wordpress">Comment Ajouter une Visite Virtuelle sur WordPress : Guide Complet 2026</a></li>
          <li><a href="/blog/fr/guide-visite-virtuelle-smartphone">Guide Complet : Créer une Visite Virtuelle 360° avec Smartphone 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-seo">SEO et Visite Virtuelle : Comment Bien Référencer vos Visites en 2026</a></li>
          <li><a href="/blog/fr/analytics-visite-virtuelle">Analytics Visite Virtuelle : Métriques Clés et Optimisation</a></li>
          <li><a href="/blog/fr/partager-photo-360-reseaux-sociaux">Comment Partager des Photos 360° sur les Réseaux Sociaux 2026</a></li>
          <li><a href="/blog/fr/visite-virtuelle-360-guide-complet">Visite Virtuelle 360° : Guide Complet pour Débutants (2026)</a></li>
          </ul>
        </section>



        <RelatedPosts
          :currentSlug="postData.slug"
          :currentCategories="postData.categories"
        />
      </article>
    </template>
  </BlogPostLayout>
</template>

<script setup>
import blogPostsData from "~/data/blogPosts.json";

const postData = blogPostsData.find(
  (p) => p.slug === "integrer-visite-virtuelle-site-web",
);

useBlogSeo({
  title:
    "Intégrer une Visite Virtuelle sur son Site Internet [2026] | Travvir",
  description:
    "Comment intégrer une visite virtuelle 360° sur son site internet : iframe, WordPress, Wix, Squarespace, performances et bonnes pratiques.",
  keywords:
    "intégrer visite virtuelle site internet, integrer visite virtuelle site internet, iframe visite virtuelle, embed tour virtuel wordpress",
  slug: "integrer-visite-virtuelle-site-web",
  image: postData?.image,
  postData,
  lang: "fr",
  faqItems: [
    {
      question: "L'intégration ralentit-elle mon site ?",
      answer:
        "Non si vous utilisez le lazy loading — la visite ne se charge qu'au moment du scroll.",
    },
    {
      question: "Puis-je intégrer plusieurs visites sur une même page ?",
      answer:
        "Techniquement oui, mais déconseillé. Utilisez des onglets pour charger une seule visite à la fois.",
    },
  ],
});

</script>
